import React, { useRef } from 'react';
import { Button } from '@fluentui/react-components';
import { Attach20Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { GuideTooltip } from '../../../../theme/components/GuideTooltip';
import {
    buildAcceptAttribute,
    formatFileSize,
    listAllowedExtensions,
    PAYMENT_RECEIPT_ALLOWED_FILE_TYPES,
    validateFile
} from '../../../../functions/AzureFileUpload.Functions';
import { AzureFileTypeEnum } from '../../../../models/Enums/AzureFileTypeEnum';
import { useInvoicesText } from './PaymentInvoices.Bits';
import './PaymentInvoices.Styles.css';

/** Error de validación (clave i18n) de un comprobante elegido, o undefined si es válido. */
export const receiptValidation = (file: Pick<File, 'name' | 'size'>): string | undefined =>
    validateFile(file, AzureFileTypeEnum.PaymentReceipt, PAYMENT_RECEIPT_ALLOWED_FILE_TYPES);

/** Los comprobantes elegidos que se pueden subir. */
export const validReceipts = (files: File[]): File[] => files.filter((file) => !receiptValidation(file));

export interface PaymentReceiptsPickerProps {
    value: File[];
    onChange: (files: File[]) => void;
    disabled?: boolean;
}

/** Elegir comprobantes antes de guardar el pago: se suben y registran cuando el pago ya tiene id. */
export const PaymentReceiptsPicker = ({ value, onChange, disabled }: PaymentReceiptsPickerProps) => {
    const tr = useInvoicesText();
    const [t] = useTranslation();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const extensions = listAllowedExtensions(PAYMENT_RECEIPT_ALLOWED_FILE_TYPES).join(', ');

    const add = (files: File[]) => {
        if (files.length === 0) return;
        const known = new Set(value.map((file) => `${file.name}|${file.size}`));
        onChange([...value, ...files.filter((file) => !known.has(`${file.name}|${file.size}`))]);
    };

    return (
        <div className="pinv-receipts" data-testid="payment-receipts-picker">
            <input
                ref={inputRef}
                className="pinv-file-input"
                type="file"
                multiple
                accept={buildAcceptAttribute(PAYMENT_RECEIPT_ALLOWED_FILE_TYPES)}
                data-testid="payment-receipts-input"
                aria-label={tr('receipts.add')}
                onChange={(event) => {
                    add(Array.from(event.target.files ?? []));
                    event.target.value = '';
                }}
            />
            <div>
                <Button appearance="secondary" icon={<Attach20Regular />} disabled={disabled} onClick={() => inputRef.current?.click()}>
                    {tr('receipts.add')}
                </Button>
            </div>
            <p className="ichtys-secondary pinv-summary">{tr('receipts.hint', { extensions })}</p>
            {value.length > 0 && (
                <ul className="pinv-file-list">
                    {value.map((file, index) => {
                        const error = receiptValidation(file);
                        return (
                            <li key={`${file.name}-${file.size}-${index}`} className="pinv-file" data-testid="payment-receipt-item">
                                <span className="pinv-file__name">
                                    {file.name}
                                    <span className="pinv-secondary ichtys-num">{formatFileSize(file.size)}</span>
                                    {error && <span className="pinv-error" role="alert">{t(error)}</span>}
                                </span>
                                <GuideTooltip content={tr('receipts.remove', { name: file.name })} relationship="label">
                                    <Button
                                        appearance="subtle"
                                        size="small"
                                        icon={<Dismiss16Regular />}
                                        disabled={disabled}
                                        onClick={() => onChange(value.filter((_item, position) => position !== index))}
                                    />
                                </GuideTooltip>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default PaymentReceiptsPicker;
